import { ComponentPropsWithoutRef } from 'react';
import * as Popover from '@givebutter/primitives/popover';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    PanelPopoverCancelButton,
    PanelPopoverIconExpandButton,
    PanelPopoverTextButton,
    ProfileItemTriggerKeys,
} from 'components/profile-panel/PanelPopoverButtons';
import {
    PanelPopoverProvider,
    usePanelPopoverContext,
} from 'components/profile-panel/PanelPopoverContext';
import { posthogEvents } from 'constants/posthog-events';
import { usePosthog } from 'customHooks/usePosthog';
import { twMerge } from 'tailwind-merge';

interface PanelPopoverRootProps extends Popover.PopoverRootProps {}
const PanelPopoverRoot = ({ children, ...rest }: PanelPopoverRootProps) => {
    const { isOpen } = usePanelPopoverContext();
    return (
        <Popover.Root open={isOpen} {...rest}>
            {children}
        </Popover.Root>
    );
};

interface PanelPopoverTriggerProps extends Popover.PopoverTriggerProps {
    profileItem: ProfileItemTriggerKeys;
}
const PanelPopoverTrigger = ({
    profileItem,
    children,
    onClick,
    ...rest
}: PanelPopoverTriggerProps) => {
    const { setIsOpen } = usePanelPopoverContext();
    const { pushEvent } = usePosthog();
    return (
        <Popover.Trigger
            {...rest}
            onClick={e => {
                setIsOpen(prev => !prev);
                pushEvent(posthogEvents.DASHBOARD_PROFILE_ITEM_SELECTED, {
                    profileItem,
                });
                onClick?.(e);
            }}
        >
            {children}
        </Popover.Trigger>
    );
};

interface PanelPopoverContentProps extends Popover.PopoverContentProps {}
const PanelPopoverContent = ({ children, className, ...rest }: PanelPopoverContentProps) => {
    const { close } = usePanelPopoverContext();
    return (
        <Popover.Portal>
            <Popover.Content
                onInteractOutside={close}
                onEscapeKeyDown={close}
                align={'start'}
                sideOffset={8}
                className={twMerge('w-[400px]', className)}
                {...rest}
            >
                {children}
            </Popover.Content>
        </Popover.Portal>
    );
};

interface PanelPopoverHeaderProps extends ComponentPropsWithoutRef<'div'> {}
const PanelPopoverHeader = ({ children, className, ...rest }: PanelPopoverHeaderProps) => {
    return (
        <div
            className={twMerge(
                'flex items-center justify-between border-b border-gray-200 p-4',
                className,
            )}
            {...rest}
        >
            {children}
        </div>
    );
};

interface PanelPopoverTitleProps extends ComponentPropsWithoutRef<'p'> {
    icon: IconDefinition;
}
const PanelPopoverTitle = ({ icon, children, className, ...rest }: PanelPopoverTitleProps) => {
    return (
        <p className={twMerge('flex items-center gap-2 text-sm font-medium', className)} {...rest}>
            <FontAwesomeIcon icon={icon} className="text-gray-500" />
            {children}
        </p>
    );
};

const PanelPopoverRootWrapper = (props: PanelPopoverRootProps) => {
    return (
        <PanelPopoverProvider>
            <PanelPopoverRoot {...props} />
        </PanelPopoverProvider>
    );
};

const Root = PanelPopoverRootWrapper;
const Trigger = PanelPopoverTrigger;
const TextButton = PanelPopoverTextButton;
const IconExpandButton = PanelPopoverIconExpandButton;
const Content = PanelPopoverContent;
const Header = PanelPopoverHeader;
const Title = PanelPopoverTitle;
const CancelButton = PanelPopoverCancelButton;

export {
    PanelPopoverRootWrapper as PanelPopoverRoot,
    PanelPopoverTrigger,
    PanelPopoverContent,
    PanelPopoverHeader,
    PanelPopoverTitle,
    //
    Root,
    Trigger,
    TextButton,
    IconExpandButton,
    Content,
    Header,
    Title,
    CancelButton,
};
